/**
 * "Biomarkers & Genomics" analytics tab for the Analysis screen.
 *
 * Fetches the full biomarker_result + genomic_alteration datasets (unified
 * `filters/data/` endpoint) and renders the purpose-built oncology views:
 * B7-H3 / PD-L1 distribution histograms, TMB-by-molecular-response scatter, MSI
 * counts, NGS alteration frequency (oncoprint-lite) and ctDNA baseline -> Cycle 3
 * kinetics. Rides alongside the standard data table as an extra tab.
 */
import { useMemo } from "react";
import type { ReactNode } from "react";

import Loader from "@/components/UI/Loader";
import ErrorComponent from "@/components/UI/Error";
import Error403 from "@/components/UI/Error403";
import { is403ApiError, useFetch } from "@/hooks/FetchHook";

import {
  CountBar,
  CtdnaSlope,
  FreqBars,
  Histogram,
  MolRespLegend,
  TmbResponseScatter,
} from "./charts";

const PAGE = { page: 1, page_size: 30000 };
const MOLRESP_ORDER = [
  "Molecular response",
  "No change",
  "Molecular progression",
  "Not evaluable",
];

type Row = Record<string, any>;

function Card({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="bg-white rounded-md shadow-omni p-4 flex flex-col">
      <h4 className="text-[13px] font-semibold text-[#2B4752] font-work-sans mb-2">
        {title}
      </h4>
      {children}
    </div>
  );
}

export function BiomarkersGenomicsTab() {
  const biomarkers = useFetch({
    endpointSuffix: "filters/data/",
    method: "POST",
    extraQueryKey: "biomarkers-charts",
    body: { dataset_type: "biomarkers", global_filters: [], pagination: PAGE },
  });
  const genomics = useFetch({
    endpointSuffix: "filters/data/",
    method: "POST",
    extraQueryKey: "genomic-charts",
    body: {
      dataset_type: "genomic_alterations",
      global_filters: [],
      pagination: PAGE,
    },
  });

  const bmRows: Row[] = (biomarkers.data as any)?.results ?? [];
  const gaRows: Row[] = (genomics.data as any)?.results ?? [];

  const charts = useMemo(() => {
    const numericByAnalyte = (name: string) =>
      bmRows
        .filter((r) => r.analyte_name === name)
        .map((r) => Number(r.value_numeric))
        .filter((v) => Number.isFinite(v));

    // subject -> molecular response (from genomic_alteration).
    const molBySubject = new Map<string, string>();
    gaRows.forEach((r) => {
      if (r.subject && r.molecular_response)
        molBySubject.set(r.subject, r.molecular_response);
    });

    // Present molecular-response groups in canonical order.
    const present = new Set(Array.from(molBySubject.values()));
    const groups = MOLRESP_ORDER.filter((g) => present.has(g));

    // TMB by molecular response.
    const tmbPoints = bmRows
      .filter((r) => r.analyte_name === "Tumor Mutational Burden")
      .map((r) => ({
        x: Number(r.value_numeric),
        group: molBySubject.get(r.subject) || "Not evaluable",
      }))
      .filter((p) => Number.isFinite(p.x));
    const tmbGroups = MOLRESP_ORDER.filter((g) =>
      tmbPoints.some((p) => p.group === g),
    );

    // MSI status counts.
    const msiCounts = new Map<string, number>();
    bmRows
      .filter((r) => r.analyte_name === "MSI Status" && r.value_text)
      .forEach((r) =>
        msiCounts.set(r.value_text, (msiCounts.get(r.value_text) || 0) + 1),
      );
    const msi = Array.from(msiCounts, ([label, count]) => ({
      label,
      count,
    })).sort((a, b) => b.count - a.count);

    // NGS alteration frequency (top genes).
    const geneCounts = new Map<string, number>();
    gaRows.forEach((r) => {
      if (r.gene) geneCounts.set(r.gene, (geneCounts.get(r.gene) || 0) + 1);
    });
    const genes = Array.from(geneCounts, ([label, count]) => ({ label, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 12);

    // ctDNA baseline -> Cycle 3 per subject.
    const bl = new Map<string, number>();
    const c3 = new Map<string, number>();
    bmRows
      .filter((r) => r.analyte_name === "ctDNA VAF")
      .forEach((r) => {
        const v = Number(r.value_numeric);
        if (!Number.isFinite(v)) return;
        if (r.timepoint === "Baseline") bl.set(r.subject, v);
        else if (r.timepoint === "Cycle 3") c3.set(r.subject, v);
      });
    const ctdna = Array.from(bl.keys())
      .filter((s) => c3.has(s))
      .map((s) => ({
        subject: s,
        baseline: bl.get(s)!,
        cycle3: c3.get(s)!,
        group: molBySubject.get(s) || "Not evaluable",
      }));
    const ctdnaGroups = MOLRESP_ORDER.filter((g) =>
      ctdna.some((s) => s.group === g),
    );

    return {
      b7h3: numericByAnalyte("B7-H3 IHC H-score"),
      pdl1Tps: numericByAnalyte("PD-L1 TPS"),
      pdl1Cps: numericByAnalyte("PD-L1 CPS"),
      tmbPoints,
      tmbGroups,
      msi,
      genes,
      ctdna,
      ctdnaGroups,
      groups,
    };
  }, [bmRows, gaRows]);

  if (biomarkers.isLoading || genomics.isLoading) return <Loader />;
  if (is403ApiError(biomarkers.error) || is403ApiError(genomics.error))
    return <Error403 />;
  if (biomarkers.isError || genomics.isError) return <ErrorComponent />;

  return (
    <div className="p-1">
      <p className="text-[12px] text-[#556C75] font-work-sans mb-3">
        {bmRows.length.toLocaleString()} biomarker results ·{" "}
        {gaRows.length.toLocaleString()} genomic alterations
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        <Card title="B7-H3 IHC H-score distribution">
          <Histogram values={charts.b7h3} xLabel="H-score" color="#64BDE1" />
        </Card>
        <Card title="PD-L1 TPS distribution">
          <Histogram values={charts.pdl1Tps} xLabel="TPS (%)" color="#7DC199" />
        </Card>
        <Card title="PD-L1 CPS distribution">
          <Histogram values={charts.pdl1Cps} xLabel="CPS" color="#C275A1" />
        </Card>
        <Card title="TMB by ctDNA molecular response">
          <TmbResponseScatter
            points={charts.tmbPoints}
            groups={charts.tmbGroups}
          />
        </Card>
        <Card title="MSI status">
          <CountBar data={charts.msi} colors={["#64BDE1", "#C275A1"]} />
        </Card>
        <Card title="NGS key alteration frequency">
          <FreqBars data={charts.genes} />
        </Card>
        <div className="md:col-span-2 xl:col-span-3">
          <Card title="ctDNA VAF kinetics — baseline to Cycle 3 (by molecular response)">
            <CtdnaSlope series={charts.ctdna} />
            <MolRespLegend groups={charts.ctdnaGroups} />
          </Card>
        </div>
      </div>
    </div>
  );
}
